const post = {
  slug: 'youtube-niches-to-avoid',
  cluster: 'niches',
  eyebrow: 'Niches',
  title: 'Faceless niches that look good and are not',
  description:
    'Five categories that get recommended constantly in faceless-channel advice, and the specific reason each one is harder than it appears.',
  keywords: [
    'youtube niches to avoid', 'bad youtube niches', 'saturated youtube niches',
    'faceless channel mistakes', 'worst youtube niches', 'youtube niche selection',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-niches-to-avoid.webp',
    alt: 'Five category cards, each stamped with the specific obstacle that makes it difficult.',
  },
  lede:
    'None of these is impossible. Each is recommended far more often than its difficulty warrants, usually by people who have not tried it — and each fails for a specific, predictable reason.',
  sections: [
    {
      heading: 'Motivational and self-improvement',
      body: [
        {
          type: 'para',
          text: 'The most recommended faceless niche and the hardest one to be visually distinct in.',
        },
        {
          type: 'para',
          text: 'The problem is the visual test. Advice about discipline has nothing concrete to show, so every video defaults to the same footage: a person running at dawn, a city timelapse, someone at a desk. Because everyone draws from the same stock libraries, the videos are literally interchangeable — the identical clips appear across hundreds of channels.',
        },
        {
          type: 'para',
          text: 'It is also a niche where the content itself is unfalsifiable and therefore hard to be specific in, which removes the other main way to differentiate.',
        },
      ],
    },
    {
      heading: 'Top 10 lists with no through-line',
      body: [
        {
          type: 'para',
          text: 'Structurally fine — each item is a chapter with its own hook. The problem is competitive rather than structural: the format is trivially cheap to produce, so supply is effectively unlimited, and the same ten items appear on every list because everyone is sourcing from the same first page of results.',
        },
        {
          type: 'para',
          text: 'It works when the items are genuinely surprising and researched. It does not work as a way to make videos quickly, which is what it is usually chosen for.',
        },
      ],
    },
    {
      heading: 'Broad "facts" and trivia',
      body: [
        {
          type: 'para',
          text: '"Amazing facts about space", "things you did not know about the ocean". Two problems compound here.',
        },
        {
          type: 'para',
          text: 'The first is that there is no reason to subscribe. A viewer who enjoyed one video has no idea what the next will be about, so the channel accumulates views without accumulating an audience.',
        },
        {
          type: 'para',
          text: 'The second is accuracy. Trivia circulates in a loop of restatement, and a large fraction of widely-repeated "facts" are wrong or heavily distorted. Building a channel on unverified claims is a slow-acting credibility problem — see [handling facts you are not sure about](/blogs/handling-uncertain-facts-in-scripts).',
        },
      ],
    },
    {
      heading: 'Reaction and commentary without the artefact',
      body: [
        {
          type: 'para',
          text: 'Covered in [which formats work](/blogs/faceless-youtube-video-formats), but it belongs here too because it is frequently pitched as a low-effort faceless niche.',
        },
        {
          type: 'para',
          text: 'On camera, commentary works because the presenter is the content. Faceless, there is nothing on screen but the thing being discussed — and if you cannot show that thing, the video is a voice over unrelated footage. There are also real rights considerations in showing material you do not own.',
        },
      ],
    },
    {
      heading: 'Anything you picked for the ad rates',
      body: [
        {
          type: 'para',
          text: 'Finance, insurance, legal and software have higher ad rates, which is true and widely known — which is why they are competitive, and why they are full of channels made by people with no interest in the subject.',
        },
        {
          type: 'para',
          text: 'These niches also punish shallowness more than most: the audience frequently knows more than the script does, and a wrong claim about tax or law is noticed immediately. And a high rate applied to a small audience is a small number. [What RPM actually means](/blogs/youtube-rpm-by-niche) puts figures around it.',
        },
        {
          type: 'quote',
          text: 'Pick the niche you can still stand at video fifty. Everything else is downstream of getting to video fifty.',
        },
      ],
    },
    {
      heading: 'The common thread',
      body: [
        {
          type: 'para',
          text: 'Every entry above fails at least one of the three constraints — usually the visual one, sometimes sustainability, occasionally both. None fails on demand, which is exactly why they keep getting recommended: demand is the easiest constraint to see and the least sufficient on its own.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'What if I genuinely love one of these topics?',
      a: 'Then the sustainability constraint is satisfied and you are in a much better position than most people entering it. Go narrower than the category — "self-improvement" is hopeless, a specific, evidenced sub-area is not.',
    },
    {
      q: 'Is any niche truly saturated?',
      a: 'Saturation in the sense of "no new channel can ever break in" is rare. What is common is a topic where supply is huge and demand per video is small, so even a good video has a low ceiling. That is worth avoiding for a different reason than competitiveness.',
    },
  ],
  related: ['faceless-youtube-niches', 'youtube-rpm-by-niche', 'narrow-youtube-niche'],
};

export default post;
